import React from 'react';
import { ICustomHeader, gridControlType } from 'app/models/Grid';
import NoteIcon from 'app/assets/images/Notes.png';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CredentialingLookup } from 'app/models/Credentialing/CredentialingLookups';
import {
  getCredentialName,
  getDate,
  getDateNoFormat,
  getDropDownValue,
  getHoverViewData,
  getLookups,
  getText,
  getBooleanSort,
  getBooleanNumber,
  getDateText,
  getCredentialNameNoFormat,
  detailColumnsAndRows,
  getMetadataForReqID,
  getCredentialNameBold,
  getDropDownDescription,
} from '../tableHelpers';
import { convertNumberToBoolean } from 'app/helpers/converters';
import { ICredentials } from 'app/models/Credentialing/Credentials';
import VersionDetailsHoverView from '../VersionDetailsHoverView';
import HistoryIcon from '@mui/icons-material/History';
import { groupBy, nestedChildren } from 'app/helpers/arrayHelpers';
import { AttachmentAction, CredentialAttachmentToolTip } from '../../Attachments/AttachmentHoverView';
import { childSorting } from '../HumanResources/HumanResourcesGroupingHelper';

const getMainTableColumns = (
  sortable: boolean = true,
  viewingDuplicates: boolean = false,
  historical: boolean = false,
) => {
  const headers: ICustomHeader[] = [
    {
      title: 'credentialName',
      value: 'Credential Name',
      sortable: sortable,
      sortByField: 'credentialNameNoFormat',
    },
    {
      title: 'verifiedID',
      value: 'Verified',
      hasHoverView: true,
      sortable: sortable,
      sortByField: 'verifiedControl',
      control: {
        type: gridControlType.checkbox,
        disabled: true,
        color: 'green',
      },
    },
    {
      title: 'issuedBy',
      value: 'Issuing Body',
      sortByField: 'issuedByDescription',
      sortable: sortable,
      control: {
        type: gridControlType.dropDown,
        disabled: true,
      },
    },
    {
      title: 'certificationRegistrationNum',
      value: 'Certification/ Registration',
      sortable: sortable,
      control: {
        type: gridControlType.textfield,
      },
    },
    {
      title: 'dateCompleted',
      value: 'Date Completed',
      sortable: sortable,
      sortByField: 'dateCompletedNoFormat',
      control: {
        type: gridControlType.date,
        disabled: true,
      },
    },
    {
      title: 'expirationDate',
      value: 'Expiration Date',
      sortable: sortable,
      defaultSort: !viewingDuplicates,
      sortByField: 'expirationDateNoFormat',
      control: {
        type: gridControlType.date,
        disabled: true,
      },
    },
    {
      title: 'lastUpdate',
      value: 'Updated Date',
      sortable: sortable,
      defaultSort: viewingDuplicates,
      sortByField: 'lastUpdateNoFormat',
      control: {
        type: gridControlType.label,
      },
    },
    {
      title: 'versioning',
      value: 'Versions',
      icon: true,
      versioning: !historical,
    },
    {
      title: 'paperlessBy',
      value: '',
      hidden: true,
      control: {
        type: gridControlType.nameID,
      },
    },
    {
      title: 'onFileByID',
      value: '',
      hidden: true,
      control: {
        type: gridControlType.nameID,
      },
    },
    {
      title: 'verifiedByID',
      value: '',
      hidden: true,
      control: {
        type: gridControlType.nameID,
      },
    },
    {
      title: 'documentIcon',
      // icon: true,
      value: 'Document',
      // clickable: true,
      navigationIds: [''],
      navigationExp: '/',
      hasHoverView: true,
      customHoverView: true,
    },
  ];
  return headers;
};

export const groupedCertifications = list => {
  return groupBy<keyof ICredentials>(
    list,
    ['credentialName'],
    [
      { item: 'expirationDate', type: 'shortDate' },
      { item: 'dateCompleted', type: 'shortDate' },
      { item: 'issuedDate', type: 'shortDate' },
      { item: 'lastUpdate', type: 'longDate' },
    ],
  );
};

const flattenData = (
  certifications: ICredentials[],
  lookups: CredentialingLookup[],
  viewingDuplicates = false,
  onAttachmentAction: (
    action: AttachmentAction,
    reqID: number,
    requirementTypeID: number,
    travelerId: number,
    brandId: number,
  ) => void,
  level: number = 0,
  expandable: boolean = true,
) => {
  let nestedItems = certifications;
  if (level === 0) {
    const grouped = groupedCertifications(certifications);
    nestedItems = nestedChildren(grouped);
  }
  if (level > 0) {
    nestedItems = childSorting(nestedItems, lookups);
  }
  const credentials: any[] = [];
  nestedItems?.forEach((item: ICredentials) => {
    const fieldLookups = getMetadataForReqID(lookups, item);
    const l: any = {
      rowID: item.requirementID,
      credentialName:
        level === 0 ? (
          getCredentialName(item.requirementType, item.credentialName)
        ) : (
          <VersionDetailsHoverView
            credential={[
              ...detailColumnsAndRows(
                item,
                fieldLookups,
                [
                  'VerifiedID',
                  'IssuedBy',
                  'CertificationRegistrationNum',
                  'DateCompleted',
                  'ExpirationDate',
                  'LastUpdate',
                ],
                true,
              ),
            ]}
            fieldLookups={fieldLookups}
            title={getCredentialName(item.requirementType, item.credentialName)}
            titleBold={getCredentialNameBold(item.requirementType, item.credentialName)}
          />
        ),
      credentialNameNoFormat: getCredentialNameNoFormat(item.requirementType, item.credentialName),
      verifiedID: getBooleanNumber(fieldLookups, 'VerifiedID', item.verifiedID),
      verifiedIDHoverViewData: getHoverViewData(fieldLookups, 'VerifiedID', convertNumberToBoolean(item.verifiedID), [
        convertNumberToBoolean(item.verifiedID)
          ? 'This credential has been verified'
          : 'This credential has not been verified',
      ]),
      verifiedControl: getBooleanSort(fieldLookups, 'VerifiedID', convertNumberToBoolean(item.verifiedID)),
      issuedBy: getDropDownValue(fieldLookups, 'IssuedBy', item.issuedBy),
      issuedByDescription: getDropDownDescription(fieldLookups, 'IssuedBy', item.issuedBy),
      issuedByFilterOptions: getLookups(fieldLookups, 'IssuedBy', item.issuedBy),
      certificationRegistrationNum: getText(
        fieldLookups,
        'CertificationRegistrationNum',
        item.certificationRegistrationNum,
      ),
      dateCompleted: getDate(fieldLookups, 'DateCompleted', item.dateCompleted),
      dateCompletedNoFormat: getDateNoFormat(fieldLookups, 'DateCompleted', item.dateCompleted),
      expirationDate: getDate(fieldLookups, 'ExpirationDate', item.expirationDate),
      expirationDateNoFormat: getDateNoFormat(fieldLookups, 'ExpirationDate', item.expirationDate),
      lastUpdate: getDateText(fieldLookups, 'LastUpdate', item.lastUpdate),
      lastUpdateNoFormat: getDateNoFormat(fieldLookups, 'LastUpdate', item.lastUpdate),
      paperlessBy: item.paperlessBy,
      onFileByID: item.onFileByID,
      versioning: level === 0 ? <HistoryIcon /> : null,
      verifiedByID: item.verifiedByID,
      documentIcon: NoteIcon,
      moreIcon: <MoreVertIcon />,
      expandable: expandable,
      detailRows: [
        [
          ...detailColumnsAndRows(item, fieldLookups, [
            'VerifiedID',
            'IssuedBy',
            'CertificationRegistrationNum',
            'DateCompleted',
            'ExpirationDate',
            'LastUpdate',
          ]),
        ],
      ],
      documentIconHoverViewComponent: (
        <CredentialAttachmentToolTip
          onSelect={onAttachmentAction}
          requirementID={item.requirementID}
          requirementTypeID={item.requirementTypeID}
          travelerId={0}
          brandId={0}
        />
      ),
      versionRows:
        level === 0 && item['children'] !== undefined
          ? flattenData(item['children'], lookups, false, onAttachmentAction, level + 1, false)
          : null,
      versionColumns: getMainTableColumns(true, false, true),
    };
    credentials.push(l);
  });
  if (level === 0) {
    return credentials.sort(
      viewingDuplicates
        ? (a: any, b: any) => (a.lastUpdateNoFormat < b.lastUpdateNoFormat ? 1 : -1)
        : (a: any, b: any) => (a.expirationDateNoFormat > b.expirationDateNoFormat ? 1 : -1),
    );
  } else return credentials;
};

export const certificationsData = {
  getTableColumns: getMainTableColumns,
  getTableRows: flattenData,
};
